import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFeatures } from '../contexts/FeaturesContext';
import { loginRouteForRole } from '../lib/roles';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, resetPassword, refreshProfile, session, profile } = useAuth();
  const { refreshFeatures } = useFeatures();
  const { isDarkMode, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [forgotPasswordInput, setForgotPasswordInput] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

    // If a Supabase session restores on app load, route the user to their landing
  // page by role. 2026-09-14 — agency-internal staff land directly on Client Hub
  // (/hub/now), not the analytics dashboard — EXCEPT staff_owner (2026-10-06) and
  // staff_manager (2026-10-07), which are toggle roles and land on /dashboard.
  // See src/lib/roles.js.
  useEffect(() => {
    if (!session || !profile) return;
    const roleName = profile?.memberships?.find?.((m) => m?.status === 'active')?.role_name
      || profile?.memberships?.[0]?.role_name;
    navigate(loginRouteForRole(roleName), { replace: true });
  }, [session, profile, navigate]);

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await signIn(email, password);
      const loadedProfile = await refreshProfile();
      await refreshFeatures();
      const primary = loadedProfile?.memberships?.find?.((m) => m?.status === 'active') || loadedProfile?.memberships?.[0];
            // Routing by role name via the shared classifier (src/lib/roles.js):
      //   client (org_viewer/org_scoped) → /client-dashboard
      //   staff  (staff_member/staff_limited) → /hub/now (Client Hub)
      //   toggle (platform_admin/org_owner/org_admin/staff_owner/staff_manager) → /dashboard
      //   2026-10-06 — staff_owner promoted to the toggle group: she now lands on
      //   /dashboard, not /hub/now.
      //   2026-10-07 — staff_manager promoted to the toggle group: same as
      //   staff_owner, now lands on /dashboard.
      navigate(loginRouteForRole(primary?.role_name));
    } catch (err) {
      setError(err?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const trimmed = forgotPasswordInput.trim();
    if (!trimmed) return;
    setForgotPasswordLoading(true);
    try {
      await resetPassword(trimmed);
    } catch (err) {
      // Don't surface "user not found" — keep the response uniform so we don't
      // leak which emails are registered.
      console.error('Password reset request failed:', err?.message);
    } finally {
      setShowForgotPasswordModal(false);
      setShowSuccessModal(true);
      setForgotPasswordInput('');
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-[100dvh] bg-white dark:bg-gray-950 transition-colors duration-200">
      {/* Logo Section - Hidden on mobile, shown on tablets and above */}
      <div className="hidden md:flex items-center justify-center bg-gray-100 dark:bg-gray-900 md:w-1/2">
        <img src={logo} alt="Insider Perks" className="max-w-xs lg:max-w-md px-4" />
      </div>

      {/* Login Form Section */}
      <div className="flex items-center justify-center w-full md:w-1/2 bg-white dark:bg-gray-950 relative px-4 py-8 md:px-6 md:py-0">
        {/* Theme Toggle Button */}
        <button
          onClick={toggleTheme}
          className="absolute top-4 right-4 md:top-6 md:right-6 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10"
          title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {isDarkMode ? (
            <svg className="w-5 h-5 md:w-6 md:h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          )}
        </button>

        {/* Mobile Logo - Shown only on mobile */}
        <div className="absolute top-8 left-1/2 transform -translate-x-1/2 md:hidden">
          <img src={logo} alt="Insider Perks" className="h-16 w-auto" />
        </div>

        {/* Login Form Card */}
        <div className="bg-white dark:bg-gray-900 shadow-lg dark:shadow-2xl rounded-lg p-6 sm:p-8 md:p-10 w-full max-w-sm border dark:border-gray-800 mt-28 md:mt-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 mb-1">Sign In Using Your Account</h2>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-6">Enter your email & password to login</p>

          <label className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="w-full mt-1 mb-4 p-2.5 sm:p-2 border dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none"
          />

          <label className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            autoComplete="current-password"
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            className="w-full mt-1 mb-4 p-2.5 sm:p-2 border dark:border-gray-700 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none"
          />

          <div className="flex items-center justify-end mb-4">
            <button
              type="button"
              onClick={() => setShowForgotPasswordModal(true)}
              className="text-xs sm:text-sm text-[#32B1FF] hover:text-[#1a9ce6] font-medium transition-colors"
            >
              Forgot Password?
            </button>
          </div>

          {error && <div className="text-red-500 dark:text-red-400 text-xs sm:text-sm mb-2 p-2 bg-red-50 dark:bg-red-900/20 rounded">{error}</div>}

          <button
            onClick={handleLogin}
            disabled={loading}
            className="bg-[#32B1FF] text-white py-2.5 sm:py-2 w-full rounded text-sm font-semibold hover:bg-[#1a9ce6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            {loading ? <div className="spinner"></div> : "Sign In"}
          </button>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-md w-full p-6 sm:p-8 relative animate-fadeIn">
            <button
              onClick={() => {
                setShowForgotPasswordModal(false);
                setForgotPasswordInput('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mb-6">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#32B1FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                Forgot Password?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enter the email on your account. We'll send a reset link.
              </p>
            </div>

            <div className="mb-6">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Email
              </label>
              <input
                type="email"
                value={forgotPasswordInput}
                onChange={(e) => setForgotPasswordInput(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                onKeyPress={(e) => e.key === 'Enter' && handleForgotPassword()}
                className="w-full p-3 border dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none"
                autoFocus
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowForgotPasswordModal(false);
                  setForgotPasswordInput('');
                }}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleForgotPassword}
                disabled={forgotPasswordLoading || !forgotPasswordInput.trim()}
                className="flex-1 px-4 py-2.5 bg-[#32B1FF] text-white rounded-lg hover:bg-[#1a9ce6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center"
              >
                {forgotPasswordLoading ? (
                  <div className="spinner"></div>
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-md w-full p-6 sm:p-8 relative animate-fadeIn">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">
                Check Your Email
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                If an account exists for the email you provided, a password reset link is on its way.
                Click the link to choose a new password.
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-200">
                <strong>Note:</strong> The link expires after a short time. Check your spam folder if you don't see it within a few minutes.
              </p>
            </div>

            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full px-4 py-2.5 bg-[#32B1FF] text-white rounded-lg hover:bg-[#1a9ce6] transition-colors font-medium text-sm"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
